import React, { useState, useEffect } from 'react';
import { getBookings, getProducts, getAddons, getFailedBookings } from '../../services/api';
import { Booking, Product, Addon } from '../../types';
import { CalendarIcon, PackageIcon, PlusIcon, DollarSignIcon, ClockIcon } from 'lucide-react';
import { theme } from '../../config/theme';
import { useNavigate } from 'react-router-dom';

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [failedBookings, setFailedBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bookingsResponse, productsData, addonsData, failedBookingsData] = await Promise.all([
          getBookings(),
          getProducts(),
          getAddons(),
          getFailedBookings()
        ]);
        
        // Extract bookings array from paginated response
        const bookingsData = bookingsResponse.items || [];
        
        // Sort bookings by created_at timestamp (newest first)
        const sortedBookings = bookingsData.sort((a: Booking, b: Booking) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        });
        setBookings(sortedBookings);
        setProducts(productsData);
        setAddons(addonsData);
        setFailedBookings(failedBookingsData || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter recent bookings to last 10 days
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
  const recentBookings = bookings
    .filter(booking => {
      if (!booking.created_at) return false;
      const createdDate = new Date(booking.created_at);
      return createdDate >= tenDaysAgo;
    })
    .slice(0, 5);
  
  // Calculate lifetime total revenue from all bookings
  const totalRevenue = bookings.reduce((sum, booking) => {
    // Only count confirmed/completed bookings
    const status = String(booking.status || '').toUpperCase();
    if (status === 'CONFIRMED' || status === 'COMPLETED') {
      // total_price is stored in cents, convert to dollars for calculation
      const totalPrice = booking.total_price ? parseFloat(String(booking.total_price)) / 100 : 0;
      return sum + totalPrice;
    }
    return sum;
  }, 0);
  
  // Calculate year-to-date stats
  const currentYear = new Date().getFullYear();
  const yearToDateBookings = bookings.filter(booking => {
    if (!booking.start_time_utc) return false;
    const bookingDate = new Date(booking.start_time_utc);
    return bookingDate.getFullYear() === currentYear;
  });
  
  const yearToDateRevenue = yearToDateBookings.reduce((sum, booking) => {
    const status = String(booking.status || '').toUpperCase();
    if (status === 'CONFIRMED' || status === 'COMPLETED') {
      const totalPrice = booking.total_price ? parseFloat(String(booking.total_price)) / 100 : 0;
      return sum + totalPrice;
    }
    return sum;
  }, 0);
  
  // Calculate partially paid bookings (those that haven't paid in full)
  const partiallyPaidBookings = bookings.filter(booking => {
    // Values are stored in cents, convert to dollars for comparison
    const totalPrice = booking.total_price ? parseFloat(String(booking.total_price)) / 100 : 0;
    const paidAmount = booking.paid_amount ? parseFloat(String(booking.paid_amount)) / 100 : 0;
    return paidAmount < totalPrice && paidAmount > 0;
  }).length;
  
  // Calculate upcoming rentals (next 7 days)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  
  const upcomingRentals = bookings
    .filter(booking => {
      if (!booking.start_time_utc) return false;
      const startDate = new Date(booking.start_time_utc);
      return startDate >= today && startDate <= sevenDaysFromNow;
    })
    .sort((a: Booking, b: Booking) => {
      const dateA = new Date(a.start_time_utc!).getTime();
      const dateB = new Date(b.start_time_utc!).getTime();
      return dateA - dateB; // Sort by closest first
    })
    .slice(0, 5); // Show top 5 upcoming rentals
  
  // Helper function to get relative date description
  const getRelativeDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    return targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-6">
      {/* Lifetime Stats Section */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Lifetime Statistics</h2>
      </div>
      
      {/* Lifetime Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
              <CalendarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Bookings</p>
              <p className="text-2xl font-semibold text-gray-900">{bookings.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-lg p-3" style={{ backgroundColor: theme.colors.semantic.warning + '20' }}>
              <CalendarIcon className="h-6 w-6" style={{ color: theme.colors.semantic.warning }} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Partially Paid Bookings</p>
              <p className="text-2xl font-semibold text-gray-900">{partiallyPaidBookings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
              <PackageIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Products</p>
              <p className="text-2xl font-semibold text-gray-900">{products.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-100 rounded-lg p-3">
              <DollarSignIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Lifetime Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">${totalRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Year-to-Date Stats Section */}
      <div className="mt-8 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Year-to-Date Statistics ({currentYear})</h2>
      </div>
      
      {/* Year-to-Date Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
              <CalendarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">YTD Bookings</p>
              <p className="text-2xl font-semibold text-gray-900">{yearToDateBookings.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
              <DollarSignIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">YTD Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">${yearToDateRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-indigo-100 rounded-lg p-3">
              <PackageIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Booking Value</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${yearToDateBookings.length > 0 ? (yearToDateRevenue / yearToDateBookings.length).toFixed(2) : '0.00'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Rentals */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Upcoming Rentals</h3>
        </div>
        <div className="overflow-hidden">
          {upcomingRentals.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {upcomingRentals.map((booking) => {
                const product = products.find(p => p.product_id === booking.product_id);
                const productName = product?.name || 'Unknown Product';
                const startDate = booking.start_time_utc ? new Date(booking.start_time_utc) : null;
                
                return (
                <div 
                  key={booking.booking_id} 
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate('/admin/bookings', { state: { selectedBookingId: booking.booking_id } })}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{booking.customer_name || booking.customer?.full_name || 'Unknown'}</p>
                      <p className="text-sm text-gray-500 truncate">{productName}</p>
                      <p className="text-xs text-gray-400">
                        {startDate ? startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2">
                      <div className="flex items-center text-xs text-gray-500">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {startDate ? getRelativeDate(startDate) : 'N/A'}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                          booking.status === 'CONFIRMED' || booking.status === 'confirmed' 
                            ? 'bg-green-100 text-green-800'
                            : booking.status === 'DROPPED_OFF' || booking.status === 'dropped_off'
                            ? 'bg-yellow-100 text-yellow-800'
                            : booking.status === 'COMPLETED' || booking.status === 'completed'
                            ? 'bg-gray-100 text-gray-800'
                            : booking.status === 'PENDING' || booking.status === 'pending'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {booking.status}
                        </span>
                        <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          ${booking.total_price ? (parseFloat(String(booking.total_price)) / 100).toFixed(2) : '0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-4 text-center text-gray-500">
              No upcoming rentals in the next 7 days
            </div>
          )}
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Bookings</h3>
        </div>
        <div className="overflow-hidden">
          {recentBookings.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {recentBookings.map((booking) => {
                const product = products.find(p => p.product_id === booking.product_id);
                const productName = product?.name || 'Unknown Product';
                
                return (
                <div 
                  key={booking.booking_id} 
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate('/admin/bookings', { state: { selectedBookingId: booking.booking_id } })}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{booking.customer_name || booking.customer?.full_name || 'Unknown'}</p>
                      <p className="text-sm text-gray-500 truncate">{productName}</p>
                      <p className="text-xs text-gray-400">
                        {booking.start_date || booking.start_time_utc ? new Date(booking.start_date || booking.start_time_utc!).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'N/A'} - {booking.end_date || booking.end_time_utc ? new Date(booking.end_date || booking.end_time_utc!).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                        booking.status === 'CONFIRMED' || booking.status === 'confirmed' 
                          ? 'bg-green-100 text-green-800'
                          : booking.status === 'DROPPED_OFF' || booking.status === 'dropped_off'
                          ? 'bg-yellow-100 text-yellow-800'
                          : booking.status === 'COMPLETED' || booking.status === 'completed'
                          ? 'bg-gray-100 text-gray-800'
                          : booking.status === 'PENDING' || booking.status === 'pending'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {booking.status}
                      </span>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        ${booking.total_price ? (parseFloat(String(booking.total_price)) / 100).toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-4 text-center text-gray-500">
              No bookings yet
            </div>
          )}
        </div>
      </div>

            {/* Failed Bookings */}
      {failedBookings.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-red-200 bg-red-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-red-900">Failed Bookings ({failedBookings.length})</h3>
              <span className="text-xs text-red-700">Requires attention</span>
            </div>
          </div>
          <div className="overflow-hidden">
            <div className="divide-y divide-red-200">
              {failedBookings.slice(0, 5).map((failedBooking: any) => {
                const createdAt = failedBooking.created_at ? new Date(failedBooking.created_at) : null;
                
                return (
                  <div 
                    key={failedBooking.failed_booking_id} 
                    className="px-6 py-4 hover:bg-red-100 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {failedBooking.customer_name || 'Unknown Customer'}
                        </p>
                        <p className="text-sm text-gray-600">{failedBooking.customer_email || 'No email'}</p>
                        <p className="text-xs text-red-600 mt-1">
                          Error: {failedBooking.error_type || 'Unknown error'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {createdAt ? createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {failedBooking.status || 'FAILED'}
                        </span>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          ${failedBooking.total_price_cents ? (failedBooking.total_price_cents / 100).toFixed(2) : '0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {failedBookings.length > 5 && (
              <div className="px-6 py-3 bg-red-50 border-t border-red-200 text-center">
                <button
                  onClick={() => navigate('/admin/bookings')}
                  className="text-sm text-red-700 hover:text-red-900 font-medium"
                >
                  View all {failedBookings.length} failed bookings →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/admin/bookings')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Booking
            </button>
            <button 
              onClick={() => navigate('/admin/products')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <PackageIcon className="h-4 w-4 mr-2" />
              Add Product
            </button>
            <button 
              onClick={() => navigate('/admin/addons')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Addon
            </button>
            <button 
              onClick={() => navigate('/admin/coupons')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Coupon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
